// (() => {
  // 'use strict';

  const SELECTORS = {
    form: '.add-to-cart-form',
    submitButton: '.product-form__submit',
    variantInput: '[name="id"]',
    existingInlineMount: '.mulberry-box',
    inlineContainer: '.mulberry-pdp-inline',
  };

  const state = {
    initialized: false,
    form: null,
    submitButton: null,
    inlineContainer: null,
    product: null,
    offers: [],
    inline: null,
    modal: null,
    selectedInlineWarranty: null,
    currentVariantId: '',
    bypassNextSubmit: false,
    busy: false,
    variantTimer: null,
    variantWatcher: null,
  };

  function wait(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  function getSettings() {
    return window.mulberry?.core?.settings || {};
  }

  function hasInlineIntegration() {
    return Boolean(getSettings().has_inline) || true;
  }

  function hasModalIntegration() {
    return Boolean(getSettings().has_modal) || true;
  }

  function findProductForm() {
    const button = document.querySelector(SELECTORS.submitButton);

    return (
      button?.closest(SELECTORS.form) ||
      document.querySelector(SELECTORS.form)
    );
  }

  function getVariantInput() {
    return state.form?.querySelector(SELECTORS.variantInput) || null;
  }

  function getCurrentVariantId() {
    const formVariantId = getVariantInput()?.value;

    const urlVariantId = new URL(
      window.location.href
    ).searchParams.get('variant');

    return String(formVariantId || urlVariantId || '').trim();
  }

  function getSelectedVariant() {
    const variantId = getCurrentVariantId();

    if (variantId) {
      window.mulberryShop.selectedVariantId = variantId;
    }

    return (
      state.product?.variants?.find(
        (variant) => String(variant.id) === String(variantId)
      ) ||
      state.product?.variants?.[0] ||
      null
    );
  }

  async function fetchOffersForCurrentVariant() {
    const selectedVariant = getSelectedVariant();

    if (!selectedVariant || !state.product) {
      return [];
    }

    window.mulberryShop.selectedVariantId = String(selectedVariant.id);

    const fetchedOffers =
      await window.mulberry.core.getWarrantyOffer({
        id: selectedVariant.id,
        title: state.product.title,
        price: selectedVariant.price,
        detail: state.product,
      });

    return Array.isArray(fetchedOffers) ? fetchedOffers : [];
  }

  function ensureInlineContainer() {
    let container = document.querySelector(
      SELECTORS.inlineContainer
    );

    if (container) {
      return container;
    }

    container = document.createElement('div');
    container.className =
      SELECTORS.inlineContainer.replace('.', '');

    container.style.minHeight = '100px';

    const existingMount = document.querySelector(
      SELECTORS.existingInlineMount
    );

    if (existingMount) {
      existingMount.appendChild(container);
    } else {
      state.form.insertAdjacentElement(
        'beforebegin',
        container
      );
    }

    return container;
  }

  function setInlineVisibility(visible) {
    if (!state.inlineContainer) {
      return;
    }

    state.inlineContainer.style.display = visible
      ? ''
      : 'none';

    state.inlineContainer.style.minHeight = visible
      ? '100px'
      : '0';
  }

  function clearInlineSelection() {
    state.selectedInlineWarranty = null;
    window.selectedOffer = null;

    try {
      state.inline?.deselectOffer?.();
    } catch (error) {
      console.warn(
        'Mulberry inline deselect failed:',
        error
      );
    }
  }

  function unwrapWarranty(warranty) {
    return warranty?.offer || warranty;
  }

  async function addWarrantyBeforeProduct(warranty) {
    const offer = unwrapWarranty(warranty);

    if (!offer) {
      throw new Error(
        'No Mulberry warranty offer was supplied.'
      );
    }

    const selectedVariant = getSelectedVariant();

    if (!selectedVariant) {
      throw new Error(
        'Unable to identify the selected Shopify variant.'
      );
    }

    window.mulberryShop.selectedVariantId =
      String(selectedVariant.id);

    const warrantyVariant =
      await window.mulberryShop.getWarrantyVariant(offer);

    if (!warrantyVariant?.shopify_variant_id) {
      throw new Error(
        'Mulberry did not return a Shopify warranty variant.'
      );
    }

    await window.mulberryShop.addWarrantyToCart(
      warrantyVariant.shopify_variant_id,
      {
        ...state.product,
        title: state.product.title,
        variantId: String(selectedVariant.id),
      },
      offer
    );
  }

  function setBusy(isBusy) {
    state.busy = isBusy;

    if (!state.submitButton) {
      return;
    }

    state.submitButton.setAttribute(
      'aria-busy',
      String(isBusy)
    );

    state.submitButton.classList.toggle(
      'mulberry-is-processing',
      isBusy
    );

    state.submitButton.disabled = isBusy;
  }

  async function runBusy(action) {
    if (state.busy) {
      return;
    }

    setBusy(true);

    try {
      await action();
    } finally {
      if (state.busy) {
        setBusy(false);
      }
    }
  }

  function submitOriginalProductForm() {
    if (!state.form) {
      return;
    }

    state.bypassNextSubmit = true;

    if (typeof state.form.requestSubmit === 'function') {
      if (state.submitButton) {
        state.submitButton.disabled = false;
      }

      state.form.requestSubmit(
        state.submitButton || undefined
      );

      return;
    }

    const submitEvent = new Event('submit', {
      bubbles: true,
      cancelable: true,
    });

    const shouldSubmitNatively =
      state.form.dispatchEvent(submitEvent);

    state.bypassNextSubmit = false;

    if (shouldSubmitNatively) {
      HTMLFormElement.prototype.submit.call(state.form);
    }
  }

  async function addWarrantyThenSubmit(warranty) {
    try {
      await addWarrantyBeforeProduct(warranty);

      clearInlineSelection();

      setBusy(false);

      submitOriginalProductForm();
    } catch (error) {
      console.error(
        'Mulberry warranty could not be added:',
        error
      );

      setBusy(false);

      submitOriginalProductForm();
    }
  }

  async function handleProtectedProductSubmit(event) {
    if (event.target !== state.form) {
      return;
    }

    if (state.bypassNextSubmit) {
      state.bypassNextSubmit = false;
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    if (state.busy) {
      return;
    }

    if (
      hasInlineIntegration() &&
      state.inline &&
      state.selectedInlineWarranty
    ) {
      await runBusy(async () => {
        await addWarrantyThenSubmit(
          state.selectedInlineWarranty
        );
      });

      return;
    }

    if (
      hasModalIntegration() &&
      state.modal &&
      state.offers.length > 0
    ) {
      state.modal.open();
      return;
    }

    submitOriginalProductForm();
  }

  async function initInline() {
    if (!hasInlineIntegration()) {
      return;
    }

    state.inlineContainer = ensureInlineContainer();

    setInlineVisibility(state.offers.length > 0);

    state.inline = await window.mulberry.inline.init({
      offers: state.offers,
      settings: getSettings(),
      selector: SELECTORS.inlineContainer,

      layout: {
        faq: false,
      },

      onWarrantyToggle: async (warranty) => {
        const isSelected = Boolean(
          warranty?.isSelected
        );

        state.selectedInlineWarranty = isSelected
          ? warranty
          : null;

        window.selectedOffer =
          state.selectedInlineWarranty;

        window.mulberry.inline.isSelected =
          isSelected;
      },
    });

    window.pdpInline = state.inline;

    if(state.offers.length > 0){
        window.mulberry.faq.init({
          coverage: window.mulberry.core.offers[0],
          settings: window.mulberry.core.settings
        })
        document.querySelector("#whats-covered-faq").removeAttribute("style")
        document.querySelector("#whats-covered-faq")?.addEventListener("click",function(){
          mulberry.faq.open();
        });
    }
  }

  async function initModal() {
    if (!hasModalIntegration()) {
      return;
    }

    state.modal = await window.mulberry.modal.init({
      offers: state.offers,
      settings: getSettings(),

      onWarrantySelect: async (warranty) => {
        state.modal?.close?.();

        await runBusy(async () => {
          await addWarrantyThenSubmit(warranty);
          setTimeout(async()=>{
            await window.mulberryShop.cart.init();
            await window.mulberryShop.cart.render();
          },100)
        });
      },

      onWarrantyDecline: async () => {
        state.modal?.close?.();

        submitOriginalProductForm();
        await removeAndSyncWarranties();
      },
    });

    window.pdpModal = state.modal;
  }

  async function initCart(){
    setTimeout(async()=>{
      await window.mulberryShop.cart.init();
      await window.mulberryShop.cart.render();
      await removeAndSyncWarranties();
    },100)
    document.addEventListener('mulberry-shopify:warranty-added', async () => {      
      await removeAndSyncWarranties();
    });
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      var unsubCart = subscribe(PUB_SUB_EVENTS.cartUpdate, async function () {
        await removeAndSyncWarranties();
      });
    }
  }

  async function rebuildCard(){
    if (window.location.href.includes('/cart')) {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
      .then((response) => response.text())
      .then(async (responseText) => {
        const html = new DOMParser().parseFromString(
          responseText,
          "text/html"
        );
        const sourceQty = html.querySelector("cart-items");
        document.querySelector("cart-items").innerHTML = sourceQty.innerHTML;
        // this.innerHTML = sourceQty.innerHTML;
        await window.mulberryShop.cart.init();
        await window.mulberryShop.cart.render();
      })
      .catch((e) => {
        console.error(e);
        setTimeout(() => {
          Rebuy.init();
        }, 100);
      });
    }else{
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then(async (responseText) => {
        const html = new DOMParser().parseFromString(
          responseText,
          "text/html"
        );
        const selectors = ["cart-drawer-items", ".cart-drawer__footer"];
        for (const selector of selectors) {
          const targetElement = document.querySelector(selector);
          const sourceElement = html.querySelector(selector);
          if (targetElement && sourceElement) {
            targetElement.replaceWith(sourceElement);
          }
        }
        await window.mulberryShop.cart.init();
        await window.mulberryShop.cart.render();
        window.mulberryShop.cart.render({ refresh: true });
        const drawer = document.getElementById("CartDrawer");
        drawer.querySelector(".drawer__heading>span").innerText = window.syncedQuantitiesCart.item_count;
      })
      .catch((e) => {
        console.error(e);
      });
    }
  }
  async function removeAndSyncWarranties (){
    const cartResponse = await fetch('/cart.js', {
      credentials: 'same-origin',
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest', // This is needed as currently there is a bug in Shopify that assumes this header
      },
    });

    const cart = await cartResponse.json();
    const detachedWarrantyCart = await window.mulberryShop.removeDetachedWarranty(cart); // Remove Warranties First
    const syncedQuantitiesCart = await window.mulberryShop.cart.syncQuantities(
      detachedWarrantyCart
    ); // Sync Quantities Second
    window.syncedQuantitiesCart = syncedQuantitiesCart;
    rebuildCard();
  }

  async function refreshOffers(force = false) {
    if (!state.product) {
      return;
    }

    const nextVariantId = getCurrentVariantId();

    if (!nextVariantId) {
      return;
    }

    if (
      !force &&
      nextVariantId === state.currentVariantId
    ) {
      return;
    }

    state.currentVariantId = nextVariantId;

    clearInlineSelection();

    await wait(250);

    state.offers =
      await fetchOffersForCurrentVariant();

    setInlineVisibility(state.offers.length > 0);

    if (state.inline?.updateOffer) {
      state.inline.updateOffer(state.offers);
    }

    if (state.modal?.updateOffer) {
      state.modal.updateOffer(state.offers);
    }
  }

  function scheduleOfferRefresh(force = false) {
    window.clearTimeout(state.variantTimer);

    state.variantTimer = window.setTimeout(() => {
      refreshOffers(force).catch((error) => {
        console.error(
          'Mulberry variant offer refresh failed:',
          error
        );
      });
    }, 100);
  }

  function watchVariantChanges() {
    state.form.addEventListener(
      'change',
      () => {
        scheduleOfferRefresh(true);
      },
      true
    );

    let lastSeenVariantId =
      getCurrentVariantId();

    state.variantWatcher = window.setInterval(() => {
      const currentVariantId =
        getCurrentVariantId();

      if (
        currentVariantId &&
        currentVariantId !== lastSeenVariantId
      ) {
        lastSeenVariantId = currentVariantId;

        scheduleOfferRefresh(true);
      }
    }, 300);
  }

  async function initMulberryPdp() {
    if (state.initialized) {
      return;
    }

    if (
      !window.location.pathname.includes('/products/')
    ) {
      return;
    }

    if (
      !window.mulberry?.core ||
      !window.mulberryShop
    ) {
      return;
    }

    state.form = findProductForm();

    state.submitButton =
      state.form?.querySelector(
        SELECTORS.submitButton
      ) ||
      document.querySelector(
        SELECTORS.submitButton
      );

    if (!state.form || !state.submitButton) {
      console.warn(
        'Mulberry PDP integration: product form or Add to Cart button was not found.'
      );

      return;
    }

    if (
      !hasInlineIntegration() &&
      !hasModalIntegration()
    ) {
      return;
    }

    state.initialized = true;

    window.mb_gtm_is_disabled = true;

    const response =
      await window.mulberryShop.getProductData(
        window.mulberryShop.getProductUrl()
      );

    state.product = response?.product;

    if (!state.product) {
      throw new Error(
        'Mulberry could not load Shopify product data.'
      );
    }

    state.currentVariantId =
      getCurrentVariantId();

    state.offers =
      await fetchOffersForCurrentVariant();

    await initInline();
    await initModal();
    document.addEventListener(
      'submit',
      handleProtectedProductSubmit,
      true
    );

    watchVariantChanges();
  }

  document.addEventListener(
    'mulberry-shopify:loaded',
    () => {
      initMulberryPdp().catch((error) => {
        console.error(
          'Mulberry PDP initialization failed:',
          error
        );
      });
      initCart().catch((error)=> {
        console.error("Mulberry cart initialization failed",error)
      });
    },
    {
      once: true,
    }
  );

  if (
    window.mulberry?.core &&
    window.mulberryShop
  ) {
    initMulberryPdp().catch((error) => {
      console.error(
        'Mulberry PDP initialization failed:',
        error
      );
    });
  }
// })();
